/**
 * AuthContext
 * Application auth/session coordinator.
 *
 * Responsibilities:
 * - subscribe to Firebase Auth state changes
 * - ensure a matching Firestore `users/{uid}` document exists
 * - expose auth actions and role/profile state to the UI
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  registerWithEmail,
  signInWithEmail,
  signInWithGooglePopup,
  signOutCurrentUser,
} from '../lib/firebase/auth';
import { auth, isFirebaseConfigured } from '../lib/firebase/config';
import { ensureUserDocument, getUserDocument } from '../services/userService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setLoading(false);
      return undefined;
    }

    let isSubscribed = true;

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!isSubscribed) {
        return;
      }

      setLoading(true);
      setAuthError('');

      try {
        if (!nextUser) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        // Guarantees role-aware profile state exists for route guards and UI.
        const userDoc = await ensureUserDocument(nextUser);
        setUser(nextUser);
        setProfile(userDoc);
      } catch (error) {
        setAuthError(error.message ?? 'Failed to load auth state.');
      } finally {
        setLoading(false);
      }
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, []);

  async function signInGoogle() {
    setAuthError('');
    const result = await signInWithGooglePopup();
    const userDoc = await ensureUserDocument(result.user);
    setUser(result.user);
    setProfile(userDoc);
    return { user: result.user, profile: userDoc };
  }

  async function signInPassword(email, password) {
    setAuthError('');
    const result = await signInWithEmail(email, password);
    const userDoc = await ensureUserDocument(result.user);
    setUser(result.user);
    setProfile(userDoc);
    return { user: result.user, profile: userDoc };
  }

  async function registerPassword(email, password) {
    setAuthError('');
    const result = await registerWithEmail(email, password);
    const userDoc = await ensureUserDocument(result.user);
    setUser(result.user);
    setProfile(userDoc);
    return { user: result.user, profile: userDoc };
  }

  async function refreshProfile() {
    if (!user) {
      return null;
    }

    const refreshed = await getUserDocument(user.uid);
    setProfile(refreshed);
    return refreshed;
  }

  async function signOut() {
    setAuthError('');
    setUser(null);
    setProfile(null);
    await signOutCurrentUser();
  }

  const value = {
    user,
    profile,
    loading,
    authError,
    signInGoogle,
    signInPassword,
    registerPassword,
    signOut,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider.');
  }
  return context;
}
